import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Layers, DoorClosed, Search } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusChip, statusTone } from "@/components/common/StatusChip";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { BulkUnitsDialog } from "@/features/properties/BulkUnitsDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const UnitsTab = ({ propertyId }) => {
  const { t } = useT();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [tipo, setTipo] = useState("all");
  const [page, setPage] = useState(1);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [singleOpen, setSingleOpen] = useState(false);
  const [form, setForm] = useState({ etiqueta: "", tipo: "apartamento", piso: "" });
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebounced(q);
      setPage(1);
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  const pageSize = 50;
  const { data, isLoading } = useQuery({
    queryKey: ["units", propertyId, debounced, tipo, page],
    queryFn: async () =>
      (
        await api.get("/units", {
          params: {
            property_id: propertyId,
            q: debounced || undefined,
            tipo: tipo === "all" ? undefined : tipo,
            page,
            page_size: pageSize,
          },
        })
      ).data,
  });

  const createMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post("/units", {
          property_id: propertyId,
          etiqueta: form.etiqueta,
          tipo: form.tipo,
          piso: form.piso === "" ? null : Number(form.piso),
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["units"] });
      toast.success(t("app.created"));
      setSingleOpen(false);
      setForm({ etiqueta: "", tipo: "apartamento", piso: "" });
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, next }) =>
      (await api.patch(`/units/${id}/status`, null, { params: { status: next } })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["units"] });
      toast.success(t("app.saved"));
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const columns = [
    { key: "etiqueta", label: t("units.label"), render: (r) => <span className="font-medium">{r.etiqueta}</span> },
    { key: "tipo", label: t("units.type"), width: 160, render: (r) => t(`units.types.${r.tipo}`) },
    { key: "piso", label: t("units.floor"), width: 90, align: "right", render: (r) => r.piso ?? "—" },
    {
      key: "status",
      label: t("app.status"),
      width: 110,
      render: (r) => <StatusChip tone={statusTone(r.status)} label={t(`status.${r.status}`)} />,
    },
    {
      key: "acciones",
      label: "",
      width: 120,
      align: "right",
      render: (r) =>
        can("units", "update") && (
          <Button
            variant="ghost"
            size="sm"
            data-testid={`unit-toggle-${r.id}`}
            onClick={() => setConfirm({ id: r.id, next: r.status === "active" ? "inactive" : "active" })}
            className="h-7 rounded-[6px] px-2 text-[12px] text-ink-tertiary"
          >
            {r.status === "active" ? t("app.deactivate") : t("app.activate")}
          </Button>
        ),
    },
  ];

  return (
    <div data-testid="units-tab">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-tertiary" />
          <Input
            data-testid="units-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("units.search")}
            className="h-8 w-[240px] rounded-[6px] bg-white pl-8 text-[13px]"
          />
        </div>
        <Select value={tipo} onValueChange={(v) => { setTipo(v); setPage(1); }}>
          <SelectTrigger data-testid="units-type-filter" className="h-8 w-[170px] rounded-[6px] bg-white text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("app.all")}</SelectItem>
            <SelectItem value="apartamento">{t("units.types.apartamento")}</SelectItem>
            <SelectItem value="area_comun">{t("units.types.area_comun")}</SelectItem>
            <SelectItem value="exterior">{t("units.types.exterior")}</SelectItem>
            <SelectItem value="amenidad">{t("units.types.amenidad")}</SelectItem>
          </SelectContent>
        </Select>
        {can("units", "create") && (
          <div className="ml-auto flex gap-2">
            <Button
              variant="outline"
              data-testid="bulk-units-button"
              onClick={() => setBulkOpen(true)}
              className="h-8 rounded-[6px] text-[13px]"
            >
              <Layers className="mr-1.5 h-3.5 w-3.5" />
              {t("units.bulk")}
            </Button>
            <Button
              data-testid="new-unit-button"
              onClick={() => setSingleOpen(true)}
              className="h-8 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              {t("units.new")}
            </Button>
          </div>
        )}
      </div>

      <div className="flex max-h-[62vh] min-h-[280px] flex-col overflow-hidden rounded-[8px] border border-line bg-white">
        <DataTable
          testId="units-table"
          columns={columns}
          rows={data?.items}
          total={data?.total || 0}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          loading={isLoading}
          empty={
            <EmptyState
              icon={DoorClosed}
              testId="units-empty"
              title={t("units.empty_title")}
              description={t("units.empty_desc")}
              action={
                can("units", "create") && (
                  <Button
                    data-testid="units-empty-bulk"
                    onClick={() => setBulkOpen(true)}
                    className="h-8 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
                  >
                    {t("units.bulk")}
                  </Button>
                )
              }
            />
          }
        />
      </div>

      <BulkUnitsDialog open={bulkOpen} onOpenChange={setBulkOpen} propertyId={propertyId} />

      <Dialog open={singleOpen} onOpenChange={setSingleOpen}>
        <DialogContent data-testid="unit-dialog" className="max-w-[420px] rounded-[8px]">
          <DialogHeader>
            <DialogTitle className="text-[16px]">{t("units.new")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3.5">
            <div>
              <Label className="mb-1.5 block text-[12px] text-ink-tertiary">{t("units.label")}</Label>
              <Input
                data-testid="unit-label-input"
                value={form.etiqueta}
                onChange={(e) => setForm((f) => ({ ...f, etiqueta: e.target.value }))}
                className="h-9 rounded-[6px] text-[13.5px]"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block text-[12px] text-ink-tertiary">{t("units.type")}</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm((f) => ({ ...f, tipo: v }))}>
                  <SelectTrigger data-testid="unit-type-select" className="h-9 rounded-[6px] text-[13.5px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apartamento">{t("units.types.apartamento")}</SelectItem>
                    <SelectItem value="area_comun">{t("units.types.area_comun")}</SelectItem>
                    <SelectItem value="exterior">{t("units.types.exterior")}</SelectItem>
                    <SelectItem value="amenidad">{t("units.types.amenidad")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-[12px] text-ink-tertiary">{t("units.floor")}</Label>
                <Input
                  data-testid="unit-floor-input"
                  type="number"
                  value={form.piso}
                  onChange={(e) => setForm((f) => ({ ...f, piso: e.target.value }))}
                  className="h-9 rounded-[6px] text-[13.5px]"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              data-testid="unit-cancel-button"
              className="h-9 rounded-[6px] text-[13px]"
              onClick={() => setSingleOpen(false)}
            >
              {t("app.cancel")}
            </Button>
            <Button
              data-testid="unit-save-button"
              disabled={!form.etiqueta.trim() || createMutation.isPending}
              onClick={() => createMutation.mutate()}
              className="h-9 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
            >
              {t("app.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(v) => !v && setConfirm(null)}
        title={confirm?.next === "inactive" ? t("confirm.deactivate_title") : t("confirm.activate_title")}
        description={confirm?.next === "inactive" ? t("confirm.deactivate_desc") : t("confirm.activate_desc")}
        onConfirm={() => {
          statusMutation.mutate(confirm);
          setConfirm(null);
        }}
      />
    </div>
  );
};
